/**
 * Color utility functions for handling hex colors and Tailwind class conversions
 */

// Tailwind class to hex color mapping
const TAILWIND_TO_HEX: Record<string, string> = {
  'bg-primary/10': '#8B5CF6',
  'bg-chart-1/10': '#8B5CF6',
  'bg-chart-2/10': '#3B82F6',
  'bg-chart-3/10': '#10B981',
  'bg-chart-4/10': '#F97316',
  'bg-chart-5/10': '#EC4899',
}

/**
 * Check if a color string is in hex format
 */
export function isHexColor(color: string): boolean {
  return /^#[0-9A-F]{6}$/i.test(color)
}

/**
 * Convert Tailwind class color to hex
 * Returns original color if already hex or not found in mapping
 */
export function convertTailwindToHex(color: string): string {
  if (isHexColor(color)) {
    return color
  }

  // Extract just the color part (e.g., "bg-primary/10 text-primary" -> "bg-primary/10")
  const colorPart = color.split(' ')[0]
  return TAILWIND_TO_HEX[colorPart] || '#8B5CF6' // Default to purple
}

/**
 * Convert hex color to rgba
 */
export function hexToRgba(hex: string, alpha: number = 1): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Lighten a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  const lighten = (value: number) => Math.min(255, Math.round(value + (255 - value) * (percent / 100)))

  const newR = lighten(r).toString(16).padStart(2, '0')
  const newG = lighten(g).toString(16).padStart(2, '0')
  const newB = lighten(b).toString(16).padStart(2, '0')

  return `#${newR}${newG}${newB}`
}

/**
 * Darken a hex color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  const darken = (value: number) => Math.max(0, Math.round(value * (1 - percent / 100)))

  const newR = darken(r).toString(16).padStart(2, '0')
  const newG = darken(g).toString(16).padStart(2, '0')
  const newB = darken(b).toString(16).padStart(2, '0')

  return `#${newR}${newG}${newB}`
}

/**
 * Generate CSS gradient from hex color for credit card design
 */
export function hexToGradient(hex: string): string {
  const baseColor = convertTailwindToHex(hex)
  const darkColor = darkenColor(baseColor, 30)
  const mediumColor = darkenColor(baseColor, 15)

  return `linear-gradient(135deg, ${darkColor} 0%, ${mediumColor} 50%, ${darkColor} 100%)`
}

/**
 * Get background style for sub-account cards
 */
export function getColorBackground(color: string): React.CSSProperties {
  const hexColor = convertTailwindToHex(color)

  return {
    backgroundColor: hexToRgba(hexColor, 0.1),
    color: hexColor,
  }
}

/**
 * Get Tailwind-compatible gradient classes from hex
 * This is used when we need to work with Tailwind's gradient system
 */
export function getGradientClasses(color: string): string {
  // If it's a Tailwind class, return gradient for it
  if (!isHexColor(color)) {
    const colorPart = color.split(' ')[0]

    switch (colorPart) {
      case 'bg-primary/10':
        return 'from-primary/90 via-primary/70 to-primary/90'
      case 'bg-chart-1/10':
        return 'from-purple-900 via-purple-800 to-purple-900'
      case 'bg-chart-2/10':
        return 'from-blue-900 via-blue-800 to-blue-900'
      case 'bg-chart-3/10':
        return 'from-green-900 via-green-800 to-green-900'
      case 'bg-chart-4/10':
        return 'from-orange-900 via-orange-800 to-orange-900'
      case 'bg-chart-5/10':
        return 'from-pink-900 via-pink-800 to-pink-900'
      default:
        return 'from-slate-900 via-slate-800 to-slate-900'
    }
  }

  // For hex colors, we'll need to use inline styles instead
  // Return empty string to signal inline style usage
  return ''
}
